import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { CustomOfferFormData, CustomOffer, ClientFeatures, ResellerFeatures } from '../types';
import {
	baseUrl,
	defaultClientFeatures,
	defaultResellerFeatures,
	VOICE_PROVIDERS,
} from '../constants';

export const useCustomOfferForm = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [offers, setOffers] = useState<CustomOffer[]>([]);
	const [generatedLink, setGeneratedLink] = useState<string>('');
	const [accountDetails, setAccountDetails] = useState<any>(null);
	const [showTrialPeriod, setShowTrialPeriod] = useState(false);
	const [showFeatures, setShowFeatures] = useState(false);
	const [showUserData, setShowUserData] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [customOfferRegistrationDataSubmitted, setCustomOfferRegistrationDataSubmitted] =
		useState<boolean>(false);

	const [formData, setFormData] = useState<CustomOfferFormData>({
		name: '',
		quantity: 1,
		priceQuote: 500,
		interval: 'month',
		intervalCount: 1,
		productName: '',
		trialPeriodDays: 7,
		saveDefault: false,
		type: 'perSeat',
		features: defaultClientFeatures,
		paymentMethod: 'stripe',
		forUserType: 'Client',
		generateAccount: false,
		generateLink: true,
		cardRequired: false,
		userData: {
			username: '',
			emailAddress: '',
			password: '',
			phoneNumber: '',
			type: 'Client',
			businessName: '',
			businessType: '',
			otherBusinessType: '',
		},
	});

	const isClient = useMemo(() => formData.forUserType === 'Client', [formData.forUserType]);
	const isWireTransfer = useMemo(
		() => formData.paymentMethod === 'wire_transfer',
		[formData.paymentMethod],
	);
	const clientFeatures = useMemo(() => formData.features as ClientFeatures, [formData.features]);
	const resellerFeatures = useMemo(
		() => formData.features as ResellerFeatures,
		[formData.features],
	);

	const updateFormData = useCallback((updates: Partial<CustomOfferFormData>) => {
		setFormData((prev) => ({ ...prev, ...updates }));
	}, []);

	useEffect(() => {
		if (formData.type === 'unlimited' && formData.quantity !== 1) {
			updateFormData({ quantity: 1 });
		}
	}, [formData.type, formData.quantity, updateFormData]);

	useEffect(() => {
		if (formData.paymentMethod !== 'wire_transfer' && formData.generateAccount) {
			updateFormData({ generateAccount: false });
			setShowUserData(false);
		}
	}, [formData.paymentMethod, formData.generateAccount, updateFormData]);

	useEffect(() => {
		if (formData.forUserType === 'Reseller' && formData.userData) {
			const resellerFields = {
				firstName: formData.userData.firstName || '',
				lastName: formData.userData.lastName || '',
				clientLocation: formData.userData.clientLocation || '',
				desiredCountry: formData.userData.desiredCountry || '',
				clientsCount: formData.userData.clientsCount || '',
				businessRevenue: formData.userData.businessRevenue || '',
				businessEmployeeSize: formData.userData.businessEmployeeSize || '',
				businessWebsite: formData.userData.businessWebsite || '',
				testedReceptionist: formData.userData.testedReceptionist || '',
				planToSell: formData.userData.planToSell || '',
			};

			updateFormData({
				userData: {
					...formData.userData,
					...resellerFields,
				},
			});
		}
	}, [formData.forUserType]);

	const updateUserData = useCallback((updates: Partial<CustomOfferFormData['userData']>) => {
		setFormData((prev) => ({
			...prev,
			userData: { ...prev.userData!, ...updates },
		}));
		setCustomOfferRegistrationDataSubmitted(true);
	}, []);

	const updateUserDataWithoutSubmission = useCallback(
		(updates: Partial<CustomOfferFormData['userData']>) => {
			setFormData((prev) => ({
				...prev,
				userData: { ...prev.userData!, ...updates },
			}));
		},
		[],
	);

	const updateFeatures = useCallback((updates: Partial<ClientFeatures>) => {
		setFormData((prev) => ({
			...prev,
			features: { ...prev.features, ...updates } as ClientFeatures,
		}));
	}, []);

	const updateVoiceProviderPricing = useCallback(
		(provider: keyof typeof VOICE_PROVIDERS, value: number) => {
			setFormData((prev) => {
				const updatedFeatures = { ...prev.features } as ClientFeatures;
				updatedFeatures.communicationAndCallHandling = {
					...updatedFeatures.communicationAndCallHandling,
					overageVoiceCharges: {
						displayText:
							updatedFeatures.communicationAndCallHandling.overageVoiceCharges.displayText,
						tooltip: updatedFeatures.communicationAndCallHandling.overageVoiceCharges.tooltip,
						charges: {
							...updatedFeatures.communicationAndCallHandling.overageVoiceCharges.charges,
							[provider]: value,
						},
					},
				};
				return { ...prev, features: updatedFeatures };
			});
		},
		[],
	);

	const getFeatureValue = useCallback(
		(path: string[]): any => {
			let current: any = clientFeatures;
			for (const key of path) {
				current = current?.[key];
				if (!current) {
					return undefined;
				}
			}
			return current?.gatedValues?.[0]?.value ?? current?.value;
		},
		[clientFeatures],
	);

	const updateFeatureValue = useCallback(
		(path: string[], value: any, useGatedValues: boolean = true) => {
			setFormData((prev) => {
				const updatedFeatures = { ...prev.features } as ClientFeatures;
				let current: any = updatedFeatures;

				for (let i = 0; i < path.length - 1; i++) {
					current = current[path[i]];
				}

				const lastKey = path[path.length - 1];

				if (useGatedValues && current[lastKey]?.gatedValues) {
					current[lastKey] = {
						...current[lastKey],
						gatedValues: [
							{ ...current[lastKey].gatedValues[0], value },
							...current[lastKey].gatedValues.slice(1),
						],
					};
				} else {
					current[lastKey] = {
						...current[lastKey],
						value,
					};
				}

				return { ...prev, features: updatedFeatures };
			});
		},
		[],
	);

	useEffect(() => {
		fetchOffers();
	}, []);

	useEffect(() => {
		if (formData.type === 'unlimited') {
			updateFormData({ quantity: 1 });
		}
	}, [formData.type, updateFormData]);

	useEffect(() => {
		if (formData.userData) {
			updateUserDataWithoutSubmission({ type: formData.forUserType });
		}
		setCustomOfferRegistrationDataSubmitted(false);
	}, [formData.forUserType, updateUserDataWithoutSubmission]);

	useEffect(() => {
		if (isClient || formData.forUserType === 'Reseller') {
			setShowFeatures(true);
		}
	}, [isClient, formData.forUserType]);

	useEffect(() => {
		if (
			formData.forUserType === 'Client' &&
			!(formData.features as ClientFeatures)?.advancedCapabilities
		) {
			updateFormData({ features: defaultClientFeatures });
		} else if (
			formData.forUserType === 'Reseller' &&
			!(formData.features as ResellerFeatures)?.voiceCredits
		) {
			updateFormData({ features: defaultResellerFeatures });
		}
	}, [formData.forUserType, updateFormData]);

	const fetchOffers = useCallback(async () => {
		try {
			setIsLoading(true);
			const token = localStorage.getItem('ADMIN_AUTH_TOKEN');
			const response = await axios.post(
				`${baseUrl}/api/custom-offers`,
				{},
				{
					headers: { Authorization: `Bearer ${token}` },
				},
			);

			if (response.data.success) {
				setOffers(response.data.data);
			}
		} catch (error) {
			console.error('Failed to fetch offers:', error);
		} finally {
			setIsLoading(false);
		}
	}, []);

	const validateForm = useCallback((): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.productName.trim()) {
			newErrors.productName = 'Product name is required';
		}

		if (!formData.generateAccount && !formData.generateLink) {
			newErrors.generateOptions = 'You must select either generate link or generate account';
		}

		if (formData.type === 'perSeat' && (!formData.quantity || formData.quantity < 1)) {
			newErrors.quantity = 'Quantity must be at least 1 for per seat type';
		}

		if (isClient && !showFeatures) {
			newErrors.features = 'Features are required for Client offers';
		}

		if (formData.generateAccount && formData.userData) {
			if (!formData.userData.username.trim()) {
				newErrors['userData.username'] = 'Username is required';
			}
			if (!formData.userData.emailAddress.trim()) {
				newErrors['userData.emailAddress'] = 'Email address is required';
			} else {
				const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
				if (!emailRegex.test(formData.userData.emailAddress)) {
					newErrors['userData.emailAddress'] = 'Please enter a valid email address';
				}
			}
			if (!formData.userData.password.trim()) {
				newErrors['userData.password'] = 'Password is required';
			} else if (formData.userData.password.length < 8) {
				newErrors['userData.password'] = 'Password must be at least 8 characters long';
			}

			if (isClient) {
				if (!formData.userData.businessName?.trim()) {
					newErrors['userData.businessName'] = 'Business name is required';
				}
				if (!formData.userData.businessType) {
					newErrors['userData.businessType'] = 'Please select your business type';
				}
				if (
					formData.userData.businessType === 'Other' &&
					!formData.userData.otherBusinessType?.trim()
				) {
					newErrors['userData.otherBusinessType'] = 'Please specify your business type';
				}
				if (formData.userData.businessWebsite?.trim()) {
					const urlPattern =
						/^https?:\/\/[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*([\/][^\s]*)?$/;
					if (!urlPattern.test(formData.userData.businessWebsite)) {
						newErrors['userData.businessWebsite'] =
							'Please enter a valid website URL (e.g., https://example.com)';
					}
				}
			}

			if (!isClient) {
				if (!formData.userData.emailAddress?.trim()) {
					newErrors['userData.emailAddress'] = 'Work email is required';
				} else {
					const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
					if (!emailRegex.test(formData.userData.emailAddress)) {
						newErrors['userData.emailAddress'] = 'Please enter a valid work email';
					} else {
						const personalDomains = [
							'gmail.com',
							'yahoo.com',
							'hotmail.com',
							'outlook.com',
							'aol.com',
						];
						const domain = formData.userData.emailAddress.split('@')[1]?.toLowerCase();
						if (personalDomains.includes(domain)) {
							newErrors['userData.emailAddress'] = 'Please use your work email address';
						}
					}
				}

				if (!formData.userData.firstName?.trim()) {
					newErrors['userData.firstName'] = 'First name is required';
				}
				if (!formData.userData.lastName?.trim()) {
					newErrors['userData.lastName'] = 'Last name is required';
				}
				if (!formData.userData.businessName?.trim()) {
					newErrors['userData.businessName'] = 'Business name is required';
				}
				if (!formData.userData.businessWebsite?.trim()) {
					newErrors['userData.businessWebsite'] = 'Website URL is required';
				} else {
					const urlPattern =
						/^https?:\/\/[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*([\/][^\s]*)?$/;
					if (!urlPattern.test(formData.userData.businessWebsite)) {
						newErrors['userData.businessWebsite'] =
							'Please enter a valid website URL (e.g., https://example.com)';
					}
				}
				if (!formData.userData.clientLocation) {
					newErrors['userData.clientLocation'] = 'Please select a client location';
				}
				if (
					formData.userData.clientLocation === 'Not listed' &&
					!formData.userData.desiredCountry
				) {
					newErrors['userData.desiredCountry'] = 'Please select your desired country';
				}
				if (!formData.userData.clientsCount) {
					newErrors['userData.clientsCount'] = 'Please select client count';
				}
				if (!formData.userData.businessType) {
					newErrors['userData.businessType'] = 'Please select business type';
				}
				if (!formData.userData.businessRevenue) {
					newErrors['userData.businessRevenue'] = 'Please select company revenue';
				}
				if (!formData.userData.businessEmployeeSize) {
					newErrors['userData.businessEmployeeSize'] = 'Please select team size';
				}
				if (!formData.userData.testedReceptionist) {
					newErrors['userData.testedReceptionist'] =
						'Please select if you have tested our receptionist';
				}
				if (!formData.userData.planToSell?.trim()) {
					newErrors['userData.planToSell'] = 'Please describe how you plan to sell our AI';
				}
			}

			if (formData.userData.type === 'SubAccount') {
				if (!formData.userData.resellerId?.trim()) {
					newErrors['userData.resellerId'] = 'Reseller ID is required for SubAccount';
				}
				if (!formData.userData.subAccountType) {
					newErrors['userData.subAccountType'] = 'SubAccount type is required';
				}
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	}, [formData, isClient, showFeatures]);

	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();

			if (!validateForm()) {
				return;
			}

			try {
				setIsLoading(true);
				const token = localStorage.getItem('ADMIN_AUTH_TOKEN');

				const apiData: any = {
					name: formData.name || undefined,
					quantity:
						formData.type === 'perSeat'
							? formData.quantity
							: formData.type === 'unlimited'
								? 1
								: undefined,
					priceQuote: formData.paymentMethod === 'wire_transfer' ? 0 : formData.priceQuote,
					interval: formData.interval,
					intervalCount: formData.intervalCount,
					productName: formData.productName,
					type: formData.type,
					forUserType: formData.forUserType,
					generateAccount: formData.generateAccount,
					generateLink: formData.generateLink,
				};

				if (showTrialPeriod && formData.trialPeriodDays) {
					apiData.trialPeriodDays = formData.trialPeriodDays;
				}

				if (showFeatures && formData.features) {
					if (isClient) {
						apiData.features = formData.features;

						if (clientFeatures?.communicationAndCallHandling?.overageVoiceCharges) {
							apiData.overageVoiceCharges =
								clientFeatures.communicationAndCallHandling.overageVoiceCharges;
						}
					} else {
						apiData.features = {
							freeCreditsAllocated: {
								voiceCredits: resellerFeatures.voiceCredits,
								smsCredits: resellerFeatures.smsCredits,
							},
						};

						if (resellerFeatures?.overageVoiceCharges) {
							apiData.features.overageVoiceCharges = resellerFeatures.overageVoiceCharges;
						}
					}
				}

				if (formData.paymentMethod) {
					apiData.paymentMethod = formData.paymentMethod;
				}

				apiData.cardRequired = formData.cardRequired;

				if (formData.generateLink) {
					apiData.saveDefault = formData.saveDefault;
				}

				if (formData.generateAccount && formData.userData) {
					const baseUserData = {
						username: formData.userData.username?.toLowerCase() || '',
						emailAddress: formData.userData.emailAddress?.toLowerCase() || '',
						businessName: formData.userData.businessName || 'na',
						password: formData.userData.password || '',
						type: formData.forUserType,
					};

					if (formData.forUserType === 'Reseller') {
						apiData.userData = {
							...baseUserData,
							clientLocation: formData.userData.clientLocation || 'na',
							firstName: formData.userData.firstName || '',
							lastName: formData.userData.lastName || '',
							clientsCount: formData.userData.clientsCount || 'Not Applicable',
							businessType: formData.userData.businessType || 'Not Applicable',
							businessRevenue: formData.userData.businessRevenue || 'Not Applicable',
							businessEmployeeSize: formData.userData.businessEmployeeSize || 'Not Applicable',
							businessWebsite: formData.userData.businessWebsite || 'Not Applicable',
							planToSell: formData.userData.planToSell || 'Not Applicable',
							testedReceptionist: formData.userData.testedReceptionist || 'Not Applicable',
						};
					} else {
						apiData.userData = {
							...baseUserData,
							phoneNumber: formData.userData.phoneNumber || '',
							businessType: formData.userData.businessType || '',
							otherBusinessType: formData.userData.otherBusinessType || '',
							agent: formData.userData.agent,
						};
					}
				}

				console.log(apiData);

				const response = await axios.post(`${baseUrl}/api/custom-offers/create`, apiData, {
					headers: { Authorization: `Bearer ${token}` },
				});

				if (response.data.success) {
					setGeneratedLink(response.data.link || '');
					setAccountDetails(response.data.accountDetails || null);
					setShowModal(true);
					await fetchOffers();
				}
			} catch (error: any) {
				console.error('Failed to generate offer:', error);
				if (error.response?.data?.errors) {
					const apiErrors: Record<string, string> = {};
					error.response.data.errors.forEach((err: any) => {
						apiErrors[err.path] = err.msg;
					});
					setErrors(apiErrors);
				} else {
					setErrors({ general: 'Failed to generate offer. Please try again.' });
				}
			} finally {
				setIsLoading(false);
			}
		},
		[formData, showTrialPeriod, showFeatures, isClient, clientFeatures, validateForm, fetchOffers],
	);

	const getFieldError = useCallback(
		(fieldName: string): string => {
			return errors[fieldName] || '';
		},
		[errors],
	);

	return {
		formData,
		isLoading,
		offers,
		generatedLink,
		accountDetails,
		showTrialPeriod,
		showFeatures,
		showUserData,
		showModal,
		errors,
		isClient,
		isWireTransfer,
		clientFeatures,
		resellerFeatures,
		customOfferRegistrationDataSubmitted,

		updateFormData,
		updateUserData,
		updateUserDataWithoutSubmission,
		updateFeatures,
		updateVoiceProviderPricing,
		getFeatureValue,
		updateFeatureValue,
		setShowTrialPeriod,
		setShowFeatures,
		setShowUserData,
		setShowModal,
		setCustomOfferRegistrationDataSubmitted,
		handleSubmit,
		getFieldError,
		fetchOffers,
	};
};
